import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { FloatingActionIslandContent } from '@/components/ui/FloatingActionIslandContent'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, MoreHorizontal } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { EvalCase } from '@super/shared-types'

interface Props {
  cases: EvalCase[]
  total: number
  limit: number
  offset: number
  onOffsetChange: (offset: number) => void
  onSelect: (c: EvalCase) => void
  onEdit: (c: EvalCase) => void
  onDelete: (id: string) => void
  isLoading: boolean
}

export function EvalCasesTable({
  cases,
  total,
  limit,
  offset,
  onOffsetChange,
  onSelect,
  onEdit,
  onDelete,
  isLoading,
}: Props) {
  const currentPage = Math.floor(offset / limit) + 1
  const totalPages = Math.ceil(total / limit)

  return (
    <div className="flex flex-col gap-3">
      <FloatingActionIslandContent
        show={totalPages > 1}
        componentId="eval-cases-pagination"
        priority={10}
      >
        <div className="inline-flex w-full lg:w-[520px] max-w-2xl">
          <div className="flex-grow items-center space-x-2 text-sm text-gray-600 whitespace-nowrap pointer-events-none hidden md:flex">
            <span className="font-medium">
              {offset + 1}–{Math.min(offset + limit, total)} of {total} cases
            </span>
          </div>
          <div className="flex items-center space-x-2 w-[240px] justify-center md:justify-end pointer-events-auto">
            <div className="inline-flex items-center space-x-1">
              <button
                onClick={() => onOffsetChange(offset - limit)}
                disabled={currentPage <= 1}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-gray-600 bg-white/80 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <div className="inline-flex items-center space-x-1 justify-end">
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNumber
                  if (totalPages <= 5) {
                    pageNumber = i + 1
                  } else if (currentPage <= 3) {
                    pageNumber = i + 1
                  } else if (currentPage >= totalPages - 2) {
                    pageNumber = totalPages - 4 + i
                  } else {
                    pageNumber = currentPage - 2 + i
                  }
                  return (
                    <button
                      key={pageNumber}
                      onClick={() => onOffsetChange((pageNumber - 1) * limit)}
                      className={cn(
                        'pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium border rounded-lg transition-all',
                        pageNumber === currentPage
                          ? 'z-10 bg-black/90 text-white border-black'
                          : 'bg-white/80 border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300',
                      )}
                    >
                      {pageNumber}
                    </button>
                  )
                })}
              </div>
              <button
                onClick={() => onOffsetChange(offset + limit)}
                disabled={currentPage >= totalPages}
                className="pointer-events-auto relative inline-flex items-center justify-center w-10 h-10 text-sm font-medium text-gray-600 bg-white/80 border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </FloatingActionIslandContent>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Prompt type</TableHead>
              <TableHead>Version</TableHead>
              <TableHead>Model</TableHead>
              <TableHead>Company</TableHead>
              <TableHead>Content</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Created</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-12" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-48" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-7 w-7 rounded-md" />
                  </TableCell>
                </TableRow>
              ))
            ) : cases.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={9}
                  className="text-center text-muted-foreground py-8"
                >
                  No eval cases yet.
                </TableCell>
              </TableRow>
            ) : (
              cases.map(c => (
                <TableRow
                  key={c.id}
                  className="cursor-pointer hover:bg-muted/50"
                  onClick={() => onSelect(c)}
                >
                  <TableCell className="font-medium">{c.name}</TableCell>
                  <TableCell>
                    <Badge variant="outline">{c.promptType}</Badge>
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {c.promptVersion ?? '—'}
                  </TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">
                    {c.model}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {c.companyName ?? (c.companyId ? c.companyId : 'Global')}
                  </TableCell>
                  <TableCell className="max-w-[240px] truncate text-sm text-muted-foreground">
                    {c.content.slice(0, 80)}
                  </TableCell>
                  <TableCell>
                    <Badge
                      variant={c.source === 'SESSION' ? 'secondary' : 'outline'}
                    >
                      {c.source}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                    {new Date(c.createdAt).toLocaleDateString()}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => onEdit(c)}>
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => onDelete(c.id)}
                        >
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
